// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Trade Goods Configuration
 * 
 * Defines tradeable commodities with base prices and location modifiers.
 * Prices vary by location distance from Sol and specialization.
 */

export const tradeGoods = {
  // Rare Minerals - Cheap at mining planets, expensive at stations
  rareMinerals: {
    id: 'rareMinerals',
    name: 'Rare Minerals',
    icon: '💎',
    description: 'Precious metals and rare earth elements extracted from asteroid fields',
    basePrice: 50,
    priceVariance: 0.3, // ±30% random variance
    weight: 1, // Cargo space per unit
    
    // Price modifiers by location type
    locationModifiers: {
      mining: 0.6,      // 40% cheaper at mining planets
      industrial: 1.2,  // 20% more expensive at industrial
      station: 1.5,     // 50% more expensive at stations
      default: 1.0
    }
  },

  // Medical Supplies - Cheap at stations, expensive at remote planets
  medicalSupplies: {
    id: 'medicalSupplies',
    name: 'Medical Supplies',
    icon: '💊',
    description: 'Advanced pharmaceuticals and medical equipment',
    basePrice: 80,
    priceVariance: 0.25,
    weight: 1,
    
    locationModifiers: {
      station: 0.7,     // 30% cheaper at stations
      remote: 1.6,      // 60% more expensive at remote planets
      mining: 1.4,      // 40% more expensive at mining
      default: 1.0
    }
  },

  // Tech Components - Cheap at industrial, expensive at mining
  techComponents: {
    id: 'techComponents',
    name: 'Tech Components',
    icon: '🔧',
    description: 'Advanced electronics and computer systems',
    basePrice: 100,
    priceVariance: 0.2,
    weight: 1,
    
    locationModifiers: {
      industrial: 0.65, // 35% cheaper at industrial
      station: 0.8,     // 20% cheaper at stations
      mining: 1.5,      // 50% more expensive at mining
      remote: 1.3,      // 30% more expensive at remote
      default: 1.0
    }
  },

  // Exotic Matter - Rare, expensive everywhere, best profits
  exoticMatter: {
    id: 'exoticMatter',
    name: 'Exotic Matter',
    icon: '⚛️',
    description: 'Unstable quantum particles used in advanced warp drives',
    basePrice: 200,
    priceVariance: 0.4, // High variance for risk/reward
    weight: 2, // Takes more cargo space
    
    locationModifiers: {
      remote: 0.7,      // 30% cheaper at remote (dangerous to get)
      station: 1.8,     // 80% more expensive at stations (high demand)
      industrial: 1.5,  // 50% more expensive at industrial
      default: 1.0
    }
  },

  // Food Rations - Cheap at agricultural, expensive at stations
  foodRations: {
    id: 'foodRations',
    name: 'Food Rations',
    icon: '🌾',
    description: 'Preserved food supplies for long-duration space travel',
    basePrice: 30,
    priceVariance: 0.15,
    weight: 1,
    
    locationModifiers: {
      agricultural: 0.5, // 50% cheaper at agricultural
      station: 1.4,      // 40% more expensive at stations
      mining: 1.3,       // 30% more expensive at mining
      remote: 1.2,       // 20% more expensive at remote
      default: 1.0
    }
  }
};

// Helper to get all trade goods as array
export const getAllTradeGoods = () => {
  return Object.values(tradeGoods);
};

// Helper to get trade good by id
export const getTradeGood = (id) => {
  return tradeGoods[id];
};

export default tradeGoods;
